import { Check } from "lucide-react"
import { useTranslation } from 'react-i18next'
import { useScrollReveal } from '../../hooks/useScrollReveal'
import { Link } from 'react-router-dom'

const popularIndex = 2
const cardDelays = ["delay-100", "delay-200", "delay-300", "delay-400"]

interface Plan {
  name: string
  tagline: string
  price: string
  priceNote: string
  cta: string
  features: string[]
}

export default function PricingSection() {
  const { t, i18n } = useTranslation()
  const headerRef = useScrollReveal<HTMLDivElement>('pricing-header', i18n.language)
  const gridRef = useScrollReveal<HTMLDivElement>('pricing-grid', i18n.language, { threshold: 0.05 })

  const plans = t('landing.pricing.plans', { returnObjects: true }) as Plan[]

  return (
    <section id="pricing" className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className="reveal-up text-center mb-12">
          <p
            className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.pricing.sectionLabel')}
          </p>
          <h2
            className="text-2xl sm:text-3xl font-bold text-gray-700 dark:text-gray-200"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t('landing.pricing.headline')}
          </h2>
        </div>

        <div
          ref={gridRef}
          className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-visible"
        >
          {plans.map((plan, idx) => (
            <div
              key={plan.name}
              className={`reveal-up ${cardDelays[idx]} relative flex flex-col ${
                idx === popularIndex
                  ? "border-2 border-green-500 rounded-lg -mx-px -my-px z-10 bg-white dark:bg-gray-800 shadow-lg"
                  : idx < plans.length - 1
                  ? "border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                  : "bg-white dark:bg-gray-900"
              }`}
            >
              {idx === popularIndex && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <span
                    className="bg-green-500 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {t('landing.pricing.mostPopular')}
                  </span>
                </div>
              )}

              <div className={`text-center px-6 pt-8 pb-4 ${idx === popularIndex ? "pt-10" : ""}`}>
                <h3
                  className="text-lg font-bold text-gray-900 dark:text-white mb-1"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {plan.name}
                </h3>
                <p
                  className="text-sm text-gray-500 dark:text-gray-400 mb-5"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {plan.tagline}
                </p>
                <div className="flex items-baseline justify-center gap-1">
                  <span
                    className="font-bold text-4xl text-gray-900 dark:text-white"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {plan.price}
                  </span>
                  {plan.priceNote && (
                    <span
                      className="text-sm text-gray-500 dark:text-gray-400"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {plan.priceNote}
                    </span>
                  )}
                </div>
              </div>

              <ul className="px-6 py-4 space-y-2.5 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <Check size={14} className="text-green-500 flex-shrink-0" />
                    <span
                      className="text-sm text-gray-600 dark:text-gray-300"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="px-6 pb-6 mt-auto">
                <Link
                  to={`/${i18n.language}/pricing`}
                  className={`block w-full py-3 px-4 text-sm font-bold transition-colors text-center rounded-lg ${
                    idx === popularIndex
                      ? "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                      : "bg-green-500 text-white hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700"
                  }`}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {plan.cta}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
